import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  Api,
  Warning,
  TrendingUp,
  Dashboard as DashboardIcon,
  Timeline,
  Notifications,
} from '@mui/icons-material';
import { SAPIconButton, MetricCard, StatusBadge, EnhancedDataCard, PageLayout } from '../components';
import { logger } from '../utils/logger';
import { mockDashboardData, mockIntegrations, mockRecentAlerts } from '../api/features/dashboard/mockDashboardData';
// Removed SAP design system import - using Material-UI theme instead
// Removed SAP styled components - using Material-UI sx props with theme instead

// Mock data moved to separate file for better organization

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(mockDashboardData);
  const [integrations, setIntegrations] = useState(mockIntegrations);
  const [alerts, setAlerts] = useState(mockRecentAlerts);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <PageLayout
        title="Dashboard"
        description="Real-time monitoring of your integrations"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: 'primary.main' }} size={48} />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Integration Dashboard"
      description="Monitor your SAP and enterprise integrations in real-time"
      actions={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SAPIconButton 
            icon="reset" 
            variant="secondary" 
            onClick={() => window.location.reload()}
          />
          <SAPIconButton 
            icon="settings" 
            variant="secondary" 
            onClick={() => logger.log('Settings clicked')}
          />
        </Box>
      }
    >
      {/* Metric Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 3, 
        mb: 4 
      }}>
        <MetricCard
          title="Total Integrations"
          value={data.integrations.total}
          icon={<Settings />}
          trend={{ value: '+2', type: 'positive' }}
          color="info"
        />
        <MetricCard
          title="Success Rate"
          value={`${data.performance.successRate}%`}
          icon={<TrendingUp />}
          trend={{ value: '+1.2%', type: 'positive' }}
          color="success"
        />
        <MetricCard
          title="Active Alerts"
          value={data.alerts.critical + data.alerts.warning}
          icon={<Warning />}
          trend={{ value: '+1', type: 'negative' }}
          color="warning"
        />
        <MetricCard
          title="Avg Response Time"
          value={`${data.performance.avgResponseTime}ms`}
          icon={<Timeline />}
          trend={{ value: '-5ms', type: 'positive' }}
          color="info"
        />
      </Box>

      {/* Data Tables */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
        gap: 3 
      }}>
        {/* Integrations Overview */}
        <EnhancedDataCard title="Integration Status" icon={<Api />} data={[]}>
          {integrations.slice(0, 6).map((integration) => (
            <Box 
              key={integration.id} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 2,
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ 
                  color: 'text.primary',
                  fontWeight: 'medium',
                }}>
                  {integration.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {integration.type} • {integration.lastSync}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StatusBadge 
                  status={integration.status === 'active' ? 'success' : 
                         integration.status === 'error' ? 'error' : 'warning'}
                >
                  {integration.status}
                </StatusBadge>
                <Typography variant="body2" sx={{ 
                  color: 'primary.main',
                  fontWeight: 'medium',
                }}>
                  {integration.successRate}%
                </Typography>
              </Box>
            </Box>
          ))}
        </EnhancedDataCard>

        {/* Recent Alerts */}
        <EnhancedDataCard title="Recent Alerts" icon={<Notifications />} data={[]}>
          {alerts.map((alert) => (
            <Box 
              key={alert.id} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                py: 2,
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <StatusBadge status={alert.type as any}>
                    {alert.type}
                  </StatusBadge>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {alert.integration}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: 'text.primary',
                  fontWeight: 'regular',
                }}>
                  {alert.message}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {alert.timestamp}
                </Typography>
              </Box>
            </Box>
          ))}
        </EnhancedDataCard>
      </Box>
    </PageLayout>
  );
}